<template>
  <Navbar />

  <div class="admin-layout">
    <SaidMenuBar />

    <div class="main-content-wrapper main-content-table same-ip-page">

      <!-- HEADER -->
      <div class="same-ip-header">
        <div>
          <h2>Same IP Accounts</h2>
          <p>
            Find linked accounts using Registration IP and Last Login IP
          </p>
        </div>

        <button class="refresh-btn" @click="loadData()">
          <i class="bi bi-arrow-clockwise"></i>
          Refresh
        </button>
      </div>

      <!-- SEARCH -->
      <div class="search-card">
        <div class="search-title">
          <i class="bi bi-search"></i>
          Search User or IP Address
        </div>

        <div class="search-row">
          <div class="search-input-wrap">
            <i class="bi bi-search search-icon"></i>

            <input
              v-model.trim="searchText"
              @keyup.enter="runSearch"
              type="text"
              placeholder="Enter Mobile Number, Login ID or IP Address"
            />

            <button
              v-if="searchText"
              class="clear-btn"
              @click="clearSearch"
              title="Clear"
            >
              ×
            </button>
          </div>

          <button class="search-btn" @click="runSearch">
            Search
          </button>
        </div>

        <div class="search-help">
          Example: 9649980862 &nbsp;•&nbsp; 223.184.252.61
        </div>
      </div>

      <!-- SUMMARY -->
      <div class="summary-grid">
        <div class="summary-card registration-summary">
          <div class="summary-icon">
            <i class="bi bi-person-plus-fill"></i>
          </div>

          <div>
            <div class="summary-label">Registration IP Groups</div>
            <div class="summary-number">
              {{ registrationGroups.length }}
            </div>
          </div>
        </div>

        <div class="summary-card login-summary">
          <div class="summary-icon">
            <i class="bi bi-box-arrow-in-right"></i>
          </div>

          <div>
            <div class="summary-label">Last Login IP Groups</div>
            <div class="summary-number">
              {{ lastLoginGroups.length }}
            </div>
          </div>
        </div>

        <div class="summary-card search-summary">
          <div class="summary-icon">
            <i class="bi bi-people-fill"></i>
          </div>

          <div>
            <div class="summary-label">Search Results</div>
            <div class="summary-number">
              {{ searchResults.length }}
            </div>
          </div>
        </div>
      </div>

      <!-- SEARCH RESULTS -->
      <div v-if="hasSearched" class="results-card search-results-card">
        <div class="section-heading">
          <div>
            <h4>Search Results</h4>
            <p>
              Results for
              <strong>"{{ lastSearch }}"</strong>
            </p>
          </div>

          <span class="count-badge">
            {{ searchResults.length }} User{{ searchResults.length === 1 ? "" : "s" }}
          </span>
        </div>

        <div v-if="searchResults.length" class="user-result-grid">
          <div
            class="user-result-card"
            v-for="user in searchResults"
            :key="user._id"
          >
            <div class="user-card-head">
              <div class="avatar-circle">
                {{ String(user.mobile || user.loginId || "?").slice(-2) }}
              </div>

              <div>
                <div class="mobile-text">
                  {{ user.mobile || "-" }}
                </div>
                <div class="login-id">
                  Login ID: {{ user.loginId || "-" }}
                </div>
              </div>
            </div>

            <div class="detail-row">
              <span>Registration IP</span>
              <strong>{{ user.register_ip || "Not Recorded" }}</strong>
            </div>

            <div class="detail-row">
              <span>Last Login IP</span>
              <strong>{{ user.last_login_ip || "Not Recorded" }}</strong>
            </div>

            <div class="detail-row">
              <span>Registered At</span>
              <strong>{{ formatDate(user.created_at) }}</strong>
            </div>

            <div class="detail-row">
              <span>Last Login</span>
              <strong>{{ formatDate(user.last_login) }}</strong>
            </div>
          </div>
        </div>

        <div v-else class="empty-state compact-empty">
          <i class="bi bi-search"></i>
          <h5>No matching user found</h5>
          <p>Try another mobile number, login ID or IP address.</p>
        </div>
      </div>

      <!-- TABS -->
      <div class="tabs-card">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'registration' }"
          @click="activeTab = 'registration'"
        >
          <i class="bi bi-person-plus"></i>
          Same Registration IP

          <span class="tab-count">
            {{ registrationGroups.length }}
          </span>
        </button>

        <button
          class="tab-btn"
          :class="{ active: activeTab === 'login' }"
          @click="activeTab = 'login'"
        >
          <i class="bi bi-box-arrow-in-right"></i>
          Same Last Login IP

          <span class="tab-count">
            {{ lastLoginGroups.length }}
          </span>
        </button>

        <button
          class="tab-btn"
          :class="{ active: activeTab === 'history' }"
          @click="activeTab = 'history'"
        >
          <i class="bi bi-clock-history"></i>
          Ever Shared Same IP

          <span class="tab-count">
            {{ historicalGroups.length }}
          </span>
        </button>
      </div>

      <!-- GROUPS -->
      <div class="groups-wrap">
        <div
          v-for="(group, index) in activeGroups"
          :key="group._id"
          class="ip-group-card"
        >
          <div class="group-header">
            <div class="ip-info">
              <div class="ip-icon">
                <i class="bi bi-globe2"></i>
              </div>

              <div>
                <div class="group-label">
                  {{
                    activeTab === "registration"
                      ? "Registration IP"
                      : activeTab === "history"
                      ? "Ever Shared IP"
                      : "Last Login IP"
                  }}
                </div>

                <div class="ip-address">
                  {{ group._id }}
                </div>
              </div>
            </div>

            <div class="account-pill">
              <i class="bi bi-people-fill"></i>
              {{ group.accountCount }}
              Account{{ group.accountCount === 1 ? "" : "s" }}
            </div>
          </div>

          <div class="group-table-wrap">
            <table class="group-table" v-if="activeTab !== 'history'">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Mobile</th>
                  <th>Login ID</th>
                  <th>Registration IP</th>
                  <th>Last Login IP</th>
                  <th>Registered At</th>
                  <th>Last Login</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(user, userIndex) in group.users"
                  :key="user.userid"
                >
                  <td>{{ userIndex + 1 }}</td>

                  <td>
                    <strong>{{ user.mobile || "-" }}</strong>
                  </td>

                  <td>{{ user.loginId || "-" }}</td>

                  <td>
                    <span
                      class="ip-chip registration-chip"
                      :class="{ missing: !user.register_ip }"
                    >
                      {{ user.register_ip || "Not Recorded" }}
                    </span>
                  </td>

                  <td>
                    <span
                      class="ip-chip login-chip"
                      :class="{ missing: !user.last_login_ip }"
                    >
                      {{ user.last_login_ip || "Not Recorded" }}
                    </span>
                  </td>

                  <td>{{ formatDate(user.created_at) }}</td>
                  <td>{{ formatDate(user.last_login) }}</td>
                </tr>
              </tbody>
            </table>

            <table class="group-table" v-else>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Mobile</th>
                  <th>Login ID</th>
                  <th>User First Seen</th>
                  <th>User Last Seen</th>
                  <th>Login Events</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(user, userIndex) in group.users"
                  :key="user.userid"
                >
                  <td>{{ userIndex + 1 }}</td>
                  <td><strong>{{ user.mobile || "-" }}</strong></td>
                  <td>{{ user.loginId || "-" }}</td>
                  <td>{{ formatDate(user.firstSeen) }}</td>
                  <td>{{ formatDate(user.lastSeen) }}</td>
                  <td>
                    <span class="ip-chip login-chip">
                      {{ user.events || 0 }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="group-footer">
            Group #{{ index + 1 }} •
            {{ group.accountCount }} linked accounts

            <span v-if="activeTab === 'history'">
              • First Seen: {{ formatDate(group.firstSeen) }}
              • Last Seen: {{ formatDate(group.lastSeen) }}
            </span>
          </div>
        </div>

        <div v-if="activeGroups.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="bi bi-diagram-3"></i>
          </div>

          <h4>
            {{
              activeTab === "registration"
                ? "No Same Registration IP Groups Yet"
                : activeTab === "history"
                ? "No Historical Same IP Groups Found"
                : "No Same Last Login IP Groups Found"
            }}
          </h4>

          <p v-if="activeTab === 'registration'">
            Registration IP tracking has recently started. New registrations
            using the same IP will automatically appear here.
          </p>

          <p v-else-if="activeTab === 'history'">
            When two or more different accounts have ever used the same IP,
            their historical connection will remain visible here even if their
            current IP addresses later change.
          </p>

          <p v-else>
            When two or more accounts share the same last login IP, their group
            will automatically appear here.
          </p>
        </div>
      </div>

      <!-- INFO -->
      <div class="info-box">
        <i class="bi bi-info-circle-fill"></i>

        <div>
          <strong>IP Review Information</strong>
          <p>
            Same IP does not automatically mean fraud. Shared Wi-Fi, mobile
            networks, offices, VPNs and family devices can also produce the
            same IP. Use this page as an investigation and contact tool.
          </p>
        </div>
      </div>

    </div>
  </div>

  <div class="loadings" v-show="loader">
    <AdminLoader />
  </div>
</template>

<script>
import axios from "axios";
import Navbar from "./Navbar.vue";
import SaidMenuBar from "./SideMenuBar.vue";
import AdminLoader from "./AdminLoaders.vue";

export default {
  name: "SameIpAccounts",

  components: {
    Navbar,
    SaidMenuBar,
    AdminLoader,
  },

  data() {
    return {
      loader: true,

      registrationGroups: [],
      lastLoginGroups: [],
      historicalGroups: [],
      searchResults: [],

      searchText: "",
      lastSearch: "",
      hasSearched: false,

      activeTab: "login",
    };
  },

  computed: {
    activeGroups() {
      if (this.activeTab === "registration") {
        return this.registrationGroups;
      }

      if (this.activeTab === "history") {
        return this.historicalGroups;
      }

      return this.lastLoginGroups;
    },
  },

  methods: {
    formatDate(value) {
      if (!value) return "-";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleString();
    },

    applyResponse(data) {
      this.registrationGroups = Array.isArray(data.registrationGroups)
        ? data.registrationGroups
        : [];

      this.lastLoginGroups = Array.isArray(data.lastLoginGroups)
        ? data.lastLoginGroups
        : [];

      this.historicalGroups = Array.isArray(data.historicalGroups)
        ? data.historicalGroups
        : [];

      this.searchResults = Array.isArray(data.searchResults)
        ? data.searchResults
        : [];
    },

    async loadData(search = "") {
      try {
        this.loader = true;

        const response = await axios.get("/admin/same-ip-accounts", {
          params: search ? { search } : {},
        });

        if (response.data && response.data.success) {
          this.applyResponse(response.data);
        }
      } catch (error) {
        console.error("Same IP Accounts Error:", error);
      } finally {
        this.loader = false;
      }
    },

    async runSearch() {
      const value = this.searchText.trim();

      if (!value) {
        this.clearSearch();
        return;
      }

      this.lastSearch = value;
      this.hasSearched = true;

      await this.loadData(value);
    },

    async clearSearch() {
      this.searchText = "";
      this.lastSearch = "";
      this.hasSearched = false;
      this.searchResults = [];

      await this.loadData();
    },
  },

  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.same-ip-page {
  padding: 18px;
  background: #f4f7fc;
  min-height: calc(100vh - 60px);
}

.same-ip-header {
  background: linear-gradient(135deg, #123d88, #2468d6);
  border-radius: 16px;
  padding: 22px 26px;
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 24px rgba(31, 83, 166, 0.18);
}

.same-ip-header h2 {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
}

.same-ip-header p {
  margin: 5px 0 0;
  opacity: 0.85;
}

.refresh-btn {
  background: #fff;
  color: #1f5bbb;
  border: 0;
  border-radius: 9px;
  padding: 10px 16px;
  font-weight: 600;
}

.search-card,
.results-card {
  margin-top: 18px;
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid #e2e9f5;
  box-shadow: 0 5px 18px rgba(30, 64, 122, 0.06);
}

.search-title {
  color: #193d75;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 12px;
}

.search-row {
  display: flex;
  gap: 10px;
}

.search-input-wrap {
  flex: 1;
  position: relative;
}

.search-input-wrap input {
  width: 100%;
  height: 44px;
  border: 1px solid #ccd8e9;
  border-radius: 9px;
  padding: 0 42px;
  outline: none;
}

.search-input-wrap input:focus {
  border-color: #3478df;
  box-shadow: 0 0 0 3px rgba(52, 120, 223, 0.1);
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 12px;
  color: #7d8ca6;
}

.clear-btn {
  position: absolute;
  right: 12px;
  top: 7px;
  border: 0;
  background: transparent;
  font-size: 24px;
  color: #8b98aa;
}

.search-btn {
  border: 0;
  border-radius: 9px;
  background: #2368d5;
  color: #fff;
  font-weight: 600;
  min-width: 110px;
}

.search-help {
  color: #8996aa;
  font-size: 12px;
  margin-top: 8px;
}

.summary-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.summary-card {
  background: #fff;
  border-radius: 13px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 15px;
  border: 1px solid #e1e8f3;
}

.summary-icon {
  height: 48px;
  width: 48px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 22px;
}

.registration-summary .summary-icon {
  background: #e7f7ee;
  color: #159455;
}

.login-summary .summary-icon {
  background: #eaf2ff;
  color: #286bcc;
}

.search-summary .summary-icon {
  background: #fff3dd;
  color: #cf8210;
}

.summary-label {
  font-size: 13px;
  color: #6d7b90;
}

.summary-number {
  font-size: 26px;
  line-height: 30px;
  font-weight: 750;
  color: #16365f;
}

.tabs-card {
  background: #fff;
  margin-top: 18px;
  border-radius: 13px;
  padding: 7px;
  display: flex;
  gap: 6px;
  border: 1px solid #e2e9f4;
}

.tab-btn {
  flex: 1;
  border: 0;
  background: transparent;
  padding: 12px;
  border-radius: 9px;
  color: #607089;
  font-weight: 600;
}

.tab-btn.active {
  background: #1d63c6;
  color: #fff;
}

.tab-count {
  display: inline-block;
  margin-left: 7px;
  min-width: 25px;
  padding: 2px 7px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.2);
}

.groups-wrap {
  margin-top: 14px;
}

.ip-group-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #dce5f2;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(31, 60, 103, 0.05);
}

.group-header {
  padding: 17px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fbff;
  border-bottom: 1px solid #e3ebf5;
}

.ip-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ip-icon {
  height: 43px;
  width: 43px;
  border-radius: 11px;
  background: #e4efff;
  color: #1b62c4;
  display: grid;
  place-items: center;
  font-size: 21px;
}

.group-label {
  color: #7d8ba0;
  font-size: 12px;
}

.ip-address {
  color: #163b70;
  font-size: 18px;
  font-weight: 700;
}

.account-pill {
  background: #eaf2ff;
  color: #1d61be;
  border-radius: 25px;
  padding: 7px 13px;
  font-size: 13px;
  font-weight: 700;
}

.group-table-wrap {
  overflow-x: auto;
}

.group-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 920px;
}

.group-table th {
  padding: 11px 13px;
  background: #f5f8fc;
  color: #53637a;
  font-size: 12px;
  text-align: left;
  border-bottom: 1px solid #e4eaf2;
}

.group-table td {
  padding: 13px;
  color: #32445e;
  font-size: 13px;
  border-bottom: 1px solid #eef2f7;
}

.ip-chip {
  display: inline-block;
  border-radius: 7px;
  padding: 5px 8px;
  font-weight: 600;
  white-space: nowrap;
}

.registration-chip {
  background: #e7f7ee;
  color: #14834d;
}

.login-chip {
  background: #e9f1ff;
  color: #1c60bb;
}

.ip-chip.missing {
  background: #f1f2f5;
  color: #9299a4;
}

.group-footer {
  padding: 10px 20px;
  color: #8794a7;
  font-size: 12px;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-heading h4 {
  color: #153b72;
  margin: 0;
}

.section-heading p {
  margin: 3px 0 0;
  color: #7a889b;
}

.count-badge {
  background: #e9f1ff;
  color: #1c61c4;
  padding: 6px 11px;
  border-radius: 20px;
  font-weight: 700;
}

.user-result-grid {
  margin-top: 15px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.user-result-card {
  border: 1px solid #e0e7f1;
  border-radius: 12px;
  padding: 15px;
}

.user-card-head {
  display: flex;
  gap: 11px;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #edf1f6;
  margin-bottom: 10px;
}

.avatar-circle {
  height: 42px;
  width: 42px;
  background: #1e64cb;
  color: #fff;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
}

.mobile-text {
  color: #153968;
  font-weight: 700;
}

.login-id {
  color: #8995a7;
  font-size: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin: 7px 0;
  font-size: 12px;
}

.detail-row span {
  color: #7b889a;
}

.detail-row strong {
  color: #32445d;
  text-align: right;
}

.empty-state {
  background: #fff;
  border: 1px dashed #ccd8e9;
  border-radius: 14px;
  padding: 45px 20px;
  text-align: center;
  color: #718096;
}

.empty-icon {
  font-size: 38px;
  color: #89a6cc;
  margin-bottom: 8px;
}

.empty-state h4,
.empty-state h5 {
  color: #31527d;
}

.compact-empty {
  margin-top: 15px;
  padding: 25px;
}

.info-box {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  background: #fff9e8;
  border: 1px solid #f0dda2;
  color: #755819;
  border-radius: 12px;
  padding: 14px 17px;
}

.info-box i {
  font-size: 19px;
  color: #c18a11;
}

.info-box p {
  margin: 3px 0 0;
  font-size: 12px;
}

@media (max-width: 900px) {
  .summary-grid,
  .user-result-grid {
    grid-template-columns: 1fr;
  }

  .same-ip-header {
    align-items: flex-start;
    gap: 15px;
    flex-direction: column;
  }

  .search-row {
    flex-direction: column;
  }

  .search-btn {
    height: 42px;
  }

  .tabs-card {
    flex-direction: column;
  }
}
</style>
